import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HubConnectionState } from '@microsoft/signalr';
import { useAuthStore } from '@/auth/auth-store';
import {
  ENCOUNTER_HUB_URL,
  createHubConnection,
} from '@/scheduling/realtime/signalr-connection';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';

/**
 * usePatientChartRealtime — wires the Chart tab to the patient-encounter
 * SignalR hub.
 *
 * **What it subscribes to**
 *
 *   1. `PatientePrescription` on `/api/patient-encounter/NotificationsHub`
 *
 *      Yes, the method name is `PatientePrescription` (lowercase "e",
 *      one word). It mirrors the backend `/api/EPriscription` route —
 *      the .NET side standardised on the typo. Flutter `meds_tab.dart:107`
 *      subscribes to the same spelling. Don't "fix" the method name; the
 *      backend never emits the corrected one.
 *
 *   2. `UpdatePatientChat` on the same hub.
 *
 *      Fan-out invalidation broadcast — backend emits a small
 *      `{ patientId, category }` envelope each time any chart-side
 *      collection changes (Flag / Allergy / Medications / Conditions /
 *      Procedure / Vaccinations / FamilyHistory / SocialHistory). The
 *      hook re-fetches the matching React Query keys when the message's
 *      patientId matches the currently viewed patient.
 *
 *      Flutter ref: `chart_main_view.dart:163-207` — same switch table,
 *      same category strings, same always-also-refresh-labs side effect.
 *      Category strings are wire-literals — copy verbatim, don't
 *      "normalize" casing.
 *
 * **What we do on event**
 *
 * `PatientePrescription` → invalidate medications + drug-interactions
 * (existing behaviour, unchanged).
 *
 * `UpdatePatientChat` → patientId-guarded category fan-out:
 *   - "Flag"          → `['patient-flags-chart', pid]`
 *   - "Allergy"       → `['patient-allergies',   pid]`
 *   - "Medications"   → `['patient-medications', pid]`  (broad-key, both active + past)
 *   - "Conditions"    → `['patient-conditions',  pid]`  (broad-key, both active + resolved)
 *   - "Procedure"     → `['patient-procedures',  pid]`
 *   - "Vaccinations"  → `['patient-immunizations', pid]`
 *   - "FamilyHistory" → `['patient-family-history', pid]`
 *   - "SocialHistory" → `['patient-social-history', pid]`
 *
 * Labs (`['patient-labs', pid]`) are invalidated on EVERY recognised
 * category. Flutter calls `loadLabs()` outside the category switch for
 * the same reason — many chart changes (e.g. an order placed under
 * "Procedure") can carry an attached lab/radiology service request.
 *
 * **Lifecycle**
 *
 * Connection is created lazily once `patientId` is known, started in
 * an effect, and stopped on unmount or patient change. Same
 * StrictMode-safe `disposed` flag dance as `useSchedulingRealtime`.
 */

/**
 * Wire shape of an `UpdatePatientChat` event.
 *
 * **Casing is camelCase, not PascalCase** — confirmed two ways:
 *   1. Flutter `realtime_patient_sumary_resp_model.dart:21-22` parses
 *      `json["patientId"]` and `json["category"]` and has been running
 *      in prod against this hub for >12 months.
 *   2. The Scrips .NET stack ships JSON globally as camelCase
 *      (`PascalCase` query-string params, camelCase response bodies);
 *      every other JSON endpoint in this codebase observes that
 *      convention.
 *
 * If a future backend change flips to PascalCase, this guard will
 * silently fail (every broadcast would invalidate every chart). Lock
 * point: the `patientId guard short-circuits cross-patient echo` test
 * fires this guard on a payload that uses the camelCase shape. A
 * PascalCase regression would fail that test.
 */
interface RealTimePatientSummaryPayload {
  patientId?: string | null;
  category?: string | null;
}

export function usePatientChartRealtime(
  patientId: string | null | undefined,
): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!patientId) return;

    const getToken = () => useAuthStore.getState().getValidAccessToken();
    // PROD-1288 / ISO H-38: the hub auto-joins the per-patient group from this query
    // param (server-side, in OnConnectedAsync) — so no `.invoke()` is needed and the
    // live Flutter apps work unchanged. Reconnects on patientId change (effect dep).
    const encounterHub = createHubConnection(
      `${ENCOUNTER_HUB_URL}?patientId=${encodeURIComponent(patientId)}`,
      getToken,
    );

    encounterHub.on('PatientePrescription', () => {
      invalidateIfNotInFlight(queryClient, ['patient-medications', patientId]);
      invalidateIfNotInFlight(queryClient, [
        'patient-drug-interactions',
        patientId,
      ]);
    });

    encounterHub.on('UpdatePatientChat', (...args: unknown[]) => {
      // The backend ships a single object as `args[0]`. Some SignalR
      // emitters wrap payloads inside an array (Flutter calls
      // `message.first`); guard against both shapes.
      //
      // Empty-array edge case: `handler([])` → `args[0]` is `[]`,
      // `Array.isArray([])` is true, `[][0]` is `undefined`, and the
      // `!raw` check below short-circuits cleanly without throwing.
      const raw = Array.isArray(args[0]) ? args[0][0] : args[0];
      if (!raw || typeof raw !== 'object') return;
      const payload = raw as RealTimePatientSummaryPayload;

      // patientId guard — the hub is per-org, not per-patient; any
      // patient editing in the same org echoes through this socket.
      // Without this guard, every broadcast in the org would nuke
      // every open chart's cache.
      if (!payload.patientId || payload.patientId !== patientId) return;

      const keyForCategory = chartCategoryToQueryKey(payload.category);
      if (!keyForCategory) return;

      // INTENTIONAL BROAD-KEY INVALIDATION. React Query treats the
      // queryKey array as a prefix match: invalidating
      // `['patient-medications', pid]` invalidates BOTH
      // `[..., true]` (active) and `[..., false]` (past) variants.
      // Same applies to `['patient-conditions', pid]` across Active +
      // Resolved status. This is deliberate — when meds change,
      // BOTH partitions can shift (e.g. a stop moves a row from
      // active → past). If a future PR adds a third query parameter
      // (page number, filter, etc.), the broad-key still works
      // correctly. DO NOT narrow this to a more specific key without
      // re-evaluating the cross-partition refresh requirement.
      invalidateIfNotInFlight(queryClient, [keyForCategory, patientId]);

      // Flutter `chart_main_view.dart:204` calls `loadLabs()` outside
      // the category switch — we mirror that. Evaluated trade-off:
      //   * Wasteful for categories that can't produce a service-
      //     request (SocialHistory, FamilyHistory, Flag).
      //   * But `invalidateQueries` only MARKS stale — it doesn't
      //     fetch unless the labs tab is currently observed. So the
      //     wasted-network cost is paid only when the user is
      //     actively viewing labs, which is the case where stale
      //     data would be most visible anyway.
      //   * Diverging from Flutter risks split parity semantics —
      //     SignalR routing changes ship from the backend team for
      //     both clients simultaneously, and a unilateral narrow
      //     here would mask a real divergence later.
      // Keep parity; revisit if React Query's invalidate cost
      // measurably regresses with a much wider query graph.
      invalidateIfNotInFlight(queryClient, ['patient-labs', patientId]);
    });

    let disposed = false;
    const startSafely = async (): Promise<void> => {
      try {
        if (encounterHub.state === HubConnectionState.Disconnected) {
          await encounterHub.start();
        }
        if (
          disposed &&
          encounterHub.state !== HubConnectionState.Disconnected
        ) {
          await encounterHub.stop();
        }
      } catch {
        // Auto-reconnect handles real failures; StrictMode-induced
        // negotiation aborts are expected dev-only noise.
      }
    };

    void startSafely();

    return () => {
      disposed = true;
      if (encounterHub.state === HubConnectionState.Connected) {
        void encounterHub.stop().catch(() => {});
      }
    };
  }, [patientId, queryClient]);
}

/**
 * Wire-category → React-Query key prefix. Strings are wire literals
 * (verbatim Flutter `chart_main_view.dart:172-200`); don't normalise.
 * Returns `null` for unknown categories — the handler skips invalidation
 * rather than nuking unrelated cache entries.
 */
function chartCategoryToQueryKey(
  category: string | null | undefined,
): string | null {
  switch (category) {
    case 'Flag':
      return 'patient-flags-chart';
    case 'Allergy':
      return 'patient-allergies';
    case 'Medications':
      return 'patient-medications';
    case 'Conditions':
      return 'patient-conditions';
    case 'Procedure':
      return 'patient-procedures';
    case 'Vaccinations':
      return 'patient-immunizations';
    case 'FamilyHistory':
      return 'patient-family-history';
    case 'SocialHistory':
      return 'patient-social-history';
    default:
      return null;
  }
}

